/**
 * Cross-tree store of the campaigns currently locked by a background operation.
 *
 * The `job_locked` / `job_unlocked` events arrive on the company-wide ATS notifications SSE, which is
 * opened once in `AtsProvider`. This tiny module store lets that stream publish which campaigns are
 * locked and lets the campaign list/edit pages read the lock at click time (`getCampaignLock`) —
 * without opening a duplicate SSE connection or adding a context provider.
 */
export interface CampaignLock {
  jobId: string;
  /** Which flow holds the lock (e.g. "candidate_upload"); used only for display copy. */
  flow?: string;
  startedAt?: string;
}

// The stream sends jobId as a string on live events but as a number on replay-on-connect events, so
// every jobId is coerced to a string before it touches the map — otherwise number-keyed locks would
// never match the campaign list's string lookups (`row.original.id.toString()`).
type JobId = string | number;
const key = (jobId: JobId): string => String(jobId);

let locks: ReadonlyMap<string, CampaignLock> = new Map();

export function lockCampaign(lock: { jobId: JobId; flow?: string; startedAt?: string }): void {
  const jobId = key(lock.jobId);
  const existing = locks.get(jobId);
  if (existing && existing.flow === lock.flow && existing.startedAt === lock.startedAt) return;
  const next = new Map(locks);
  next.set(jobId, { jobId, flow: lock.flow, startedAt: lock.startedAt });
  locks = next;
}

export function unlockCampaign(jobId: JobId): void {
  const id = key(jobId);
  if (!locks.has(id)) return;
  const next = new Map(locks);
  next.delete(id);
  locks = next;
}

/** Reads the current lock for a campaign (used by mutation handlers at click time). */
export function getCampaignLock(jobId: JobId): CampaignLock | undefined {
  return locks.get(key(jobId));
}

export type BusyKind = 'atsSyncing' | 'adding' | 'rescoring';

export interface BusyCopy {
  title: string;
  message: string;
}

// Single source of truth for the "campaign busy" modal copy, shared by the list, detail and edit pages.
export const BUSY_COPY: Record<BusyKind, BusyCopy> = {
  atsSyncing: {
    title: 'ATS Syncing',
    message: 'Syncing with your ATS right now, campaign editing will be back in just a moment.',
  },
  adding: {
    title: 'Adding Candidates',
    message: "Adding candidates to this campaign, Once they land, you're free to edit.",
  },
  rescoring: {
    title: 'Rescoring Candidates',
    message: "Candidates are being rescored. Give it a moment and you'll be able to edit again.",
  },
};

const DEFAULT_BUSY_COPY: BusyCopy = {
  title: 'Campaign busy',
  message: 'This campaign is currently busy. Please wait until it completes.',
};

// Maps a lock's `flow` (from the job_locked event) to the modal copy. Matched loosely so backend
// naming variations still resolve correctly.
export function busyCopyForFlow(flow?: string): BusyCopy {
  const f = (flow ?? '').toLowerCase();
  if (f.includes('ats')) return BUSY_COPY.atsSyncing;
  if (f.includes('rescore') || f.includes('re-score') || f.includes('score')) return BUSY_COPY.rescoring;
  if (f.includes('upload') || f.includes('add') || f.includes('candidate')) return BUSY_COPY.adding;
  return DEFAULT_BUSY_COPY;
}

/** Modal copy for the detail page, keyed by the derived activity rather than a flow string. */
export function busyCopyForActivity(activity: BusyKind): BusyCopy {
  return BUSY_COPY[activity];
}
